import React, { useEffect, useMemo, useState } from 'react';
import { Database, Loader2, Plus, Trash2, X } from 'lucide-react';
import { axiosInstance } from '../App';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';

const KnowledgeKeywordGroupPicker = ({ value = [], onChange, compact = false }) => {
  const [groups, setGroups] = useState([]);
  const [canManage, setCanManage] = useState(false);
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [terms, setTerms] = useState('');
  const [color, setColor] = useState('#0f766e');

  const load = async () => {
    const [groupsResult, capabilityResult] = await Promise.allSettled([
      axiosInstance.get('/knowledge-keyword-groups'),
      axiosInstance.get('/upload-taxonomy/capabilities'),
    ]);
    if (groupsResult.status === 'fulfilled') {
      const groupsResponse = groupsResult.value;
      setGroups(groupsResponse.data || []);
    }
    if (capabilityResult.status === 'fulfilled') {
      const capabilityResponse = capabilityResult.value;
      setCanManage(Boolean(capabilityResponse.data?.can_manage));
    }
  };

  useEffect(() => { load(); }, []);
  const groupIds = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const selected = useMemo(() => new Set(groupIds), [groupIds]);
  const toggle = (groupId) => onChange?.(selected.has(groupId) ? groupIds.filter(id => id !== groupId) : [...groupIds, groupId]);

  const create = async () => {
    if (!name.trim() || !terms.trim() || saving) return;
    setSaving(true);
    try {
      const response = await axiosInstance.post('/knowledge-keyword-groups', { name: name.trim(), color, terms });
      const group = response.data;
      setGroups(current => [...current, group].sort((a, b) => a.name.localeCompare(b.name)));
      onChange?.([...groupIds, group.id]);
      setName('');
      setTerms('');
      setCreating(false);
      toast.success(`Keyword group “${group.name}” created`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not create keyword group');
    } finally {
      setSaving(false);
    }
  };

  const archive = async (group) => {
    try {
      await axiosInstance.delete(`/knowledge-keyword-groups/${group.id}`);
      setGroups(current => current.filter(item => item.id !== group.id));
      onChange?.(groupIds.filter(id => id !== group.id));
      toast.success('Keyword group archived');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Could not archive keyword group');
    }
  };

  return (
    <div className={compact ? 'space-y-2' : 'space-y-3'} data-testid="knowledge-keyword-group-picker">
      <div className="flex items-center justify-between gap-3">
        <div>
          <Label className="flex items-center gap-1.5 text-xs"><Database size={13} /> Database tags / search keywords <span className="font-normal text-slate-400">Optional</span></Label>
          {!compact && <p className="mt-1 text-xs text-slate-500">Choose a top group. All pasted terms inside it become searchable.</p>}
        </div>
        {canManage && <Button type="button" size="sm" variant="outline" onClick={() => setCreating(current => !current)}>{creating ? <X size={13} /> : <Plus size={13} />} {creating ? 'Cancel' : 'New group'}</Button>}
      </div>
      {creating && (
        <div className="space-y-2 rounded-lg border border-teal-100 bg-teal-50 p-3">
          <div className="flex gap-2">
            <Input value={name} onChange={event => setName(event.target.value)} placeholder="Top category, e.g. Welfare Benefits" maxLength={100} className="bg-white" />
            <label className="flex h-9 w-11 shrink-0 items-center rounded-md border bg-white p-1" title="Group colour"><input type="color" value={color} onChange={event => setColor(event.target.value)} className="h-full w-full cursor-pointer" aria-label="Keyword group colour" /></label>
          </div>
          <Textarea value={terms} onChange={event => setTerms(event.target.value)} placeholder={'Debt\nBenefits\nDisability\nEquality Act'} className="min-h-24 bg-white" />
          <p className="text-[11px] text-slate-500">Paste from a spreadsheet. New lines, commas, semicolons and tabbed cells are separated automatically.</p>
          <Button type="button" size="sm" onClick={create} disabled={!name.trim() || !terms.trim() || saving}>{saving ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />} Create and select</Button>
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        {!groups.length && <span className="text-xs text-slate-400">{canManage ? 'No keyword groups yet. Create the first one.' : 'No keyword groups are available.'}</span>}
        {groups.map(group => {
          const active = selected.has(group.id);
          return (
            <span key={group.id} className="inline-flex items-center rounded-full border" style={{ borderColor: group.color, backgroundColor: active ? group.color : 'white', color: active ? 'white' : group.color }}>
              <button type="button" className="px-2.5 py-1 text-xs font-medium" onClick={() => toggle(group.id)} aria-pressed={active} title={(group.terms || []).join(', ')}>{group.name} ({(group.terms || []).length})</button>
              {canManage && <button type="button" className="pr-2 opacity-70 hover:opacity-100" aria-label={`Archive ${group.name}`} onClick={() => archive(group)}><Trash2 size={11} /></button>}
            </span>
          );
        })}
      </div>
    </div>
  );
};

export default KnowledgeKeywordGroupPicker;
